import React, { useEffect, useMemo } from 'react';
import { Alert } from 'antd';
import styled from 'styled-components';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';

const RulesContainer = styled.div`
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 8px 0;
`;

const RulesTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 8px;
  color: #333;
`;

const RuleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ $met }) => ($met ? '#198038' : '#666')};
  margin: 4px 0;

  .anticon {
    font-size: 14px;
  }
`;

const CriteriaNote = styled.div`
  font-size: 11px;
  color: #888;
  margin-top: 8px;
  font-style: italic;
`;

const RuleIcon = ({ met }) =>
  met ? (
    <CheckCircleFilled style={{ color: '#198038' }} />
  ) : (
    <CloseCircleFilled style={{ color: '#d9d9d9' }} />
  );

export const SERVER_ERROR_MESSAGES = {
  PASSWORD_TOO_SHORT: 'Make sure that the new password meets the requirements.',
  PASSWORD_TOO_WEAK: 'Make sure that the new password meets the requirements.',
  PASSWORD_NOT_SECURE:
    'This password has appeared in a known data breach. Please choose a different password.',
  BAD_CURRENT_PASSWORD: 'Make sure that the current password entry is correct.',
};

const getServerErrorMessage = error =>
  SERVER_ERROR_MESSAGES[error?.message] ||
  'It was not possible to change your password, please try again later.';

const PasswordError = ({
  newPass,
  confirmPass,
  onChange,
  shouldRender,
  serverError,
  passwordSecurityError,
}) => {
  // Compute all validation state from props - no local state needed
  const validation = useMemo(() => {
    const hasUppercase = /[A-Z]/.test(newPass);
    const hasLowercase = /[a-z]/.test(newPass);
    const hasNumber = /\d/.test(newPass);
    const hasSpecialChar = /[!@#$%^&*()_+[\]{};':"\\|,.<>?]/.test(newPass);

    const criteriaCount =
      Number(hasUppercase) +
      Number(hasLowercase) +
      Number(hasNumber) +
      Number(hasSpecialChar);

    const lengthMet = newPass?.length >= 8;
    const isTooShort = newPass && !lengthMet;
    const isTooWeak = criteriaCount < 3;
    const passwordsMatch = newPass && confirmPass && newPass === confirmPass;
    const passwordsMismatch = newPass && confirmPass && newPass !== confirmPass;

    return {
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      lengthMet,
      isTooShort,
      isTooWeak,
      passwordsMatch,
      passwordsMismatch,
      hasErrors: isTooShort || isTooWeak || passwordsMismatch,
    };
  }, [newPass, confirmPass]);

  // Notify parent of validation state changes
  useEffect(() => {
    onChange({
      hasErrors: validation.hasErrors,
      isTooShort: validation.isTooShort,
      isTooWeak: validation.isTooWeak,
    });
  }, [
    onChange,
    validation.hasErrors,
    validation.isTooShort,
    validation.isTooWeak,
  ]);

  const serverErrorMessage =
    serverError && !validation.hasErrors
      ? getServerErrorMessage(serverError)
      : null;
  const showMatchStatus = confirmPass?.length > 0;

  return (
    <React.Fragment>
      {shouldRender && (
        <RulesContainer>
          <RulesTitle>Password Requirements</RulesTitle>
          <RuleItem $met={validation.lengthMet}>
            <RuleIcon met={validation.lengthMet} />
            <span>At least 8 characters</span>
          </RuleItem>
          <RuleItem $met={validation.hasUppercase}>
            <RuleIcon met={validation.hasUppercase} />
            <span>Uppercase letter (A-Z)</span>
          </RuleItem>
          <RuleItem $met={validation.hasLowercase}>
            <RuleIcon met={validation.hasLowercase} />
            <span>Lowercase letter (a-z)</span>
          </RuleItem>
          <RuleItem $met={validation.hasNumber}>
            <RuleIcon met={validation.hasNumber} />
            <span>Number (0-9)</span>
          </RuleItem>
          <RuleItem $met={validation.hasSpecialChar}>
            <RuleIcon met={validation.hasSpecialChar} />
            <span>Special character (!@#$%^&*...)</span>
          </RuleItem>
          {showMatchStatus && (
            <RuleItem $met={validation.passwordsMatch}>
              <RuleIcon met={validation.passwordsMatch} />
              <span>Passwords match</span>
            </RuleItem>
          )}
          <CriteriaNote>
            Must meet length requirement + at least 3 of the 4 character types
          </CriteriaNote>
        </RulesContainer>
      )}
      {passwordSecurityError && (
        <Alert
          message={passwordSecurityError}
          type='error'
          style={{ marginTop: 8 }}
        />
      )}
      {serverErrorMessage && !passwordSecurityError && (
        <Alert
          message={serverErrorMessage}
          type='error'
          style={{ marginTop: 8 }}
        />
      )}
    </React.Fragment>
  );
};

export default PasswordError;
